import { ELEMENT_LABEL, type Element } from '../core/constants.ts';
import { SPELLS_BY_ID, displayName } from '../core/spells.ts';
import type { ActiveEffect } from '../core/player.ts';

/**
 * What is currently on you, as a glyph.
 *
 * Drawn rather than named because these have to be read in the middle of a
 * fight: whether you are still guarded, and against what, is a question you ask
 * with a glance and not by reading a list. The element carries most of the
 * meaning, so it carries the colour.
 */

/** 24×24 stroke paths, drawn on a common grid so they sit together. */
const GLYPH = {
  flame:
    'M12 3.5c2.8 3.8 4.8 6 4.8 9a4.8 4.8 0 0 1-9.6 0c0-1.8.8-3 1.9-4 '
    + '.1 1 .7 1.8 1.5 2.1-.4-2.8.4-5 1.4-7.1z',
  drop: 'M12 3.5c2.9 4.8 4.8 7 4.8 9.6a4.8 4.8 0 0 1-9.6 0C7.2 10.5 9.1 8.3 12 3.5z',
  shield: 'M12 3.2l6.6 2.7v5.6c0 3.9-2.8 6.9-6.6 8.7-3.8-1.8-6.6-4.8-6.6-8.7V5.9z',
  shieldInner: 'M12 7l3.4 1.4v2.9c0 2-1.4 3.6-3.4 4.5-2-.9-3.4-2.5-3.4-4.5V8.4z',
  halo: 'M12 7.4a4.6 4.6 0 1 0 0 9.2 4.6 4.6 0 0 0 0-9.2z',
  rays: 'M12 2.4v2.6M12 19v2.6M2.4 12h2.6M19 12h2.6M5.2 5.2l1.8 1.8M17 17l1.8 1.8M18.8 5.2L17 7M7 17l-1.8 1.8',
  chevrons: 'M6.5 7.5L12 12l5.5-4.5M6.5 13L12 17.5l5.5-4.5',
  shackle: 'M4.5 12h15M8 7.5v9M16 7.5v9',
  crack: 'M13.5 2.8L8.5 11h4l-3 10.2M6 8l2.5 1M18 14l-2.5 1',
} as const;

type Tone = 'guard' | 'harm';

export interface StatusBadge {
  key: string;
  label: string;
  tone: Tone;
  element?: Element;
  svg: string;
  /** 0..1 of its original life. */
  fraction: number;
  seconds: number;
}

const stroke = (d: string, extra = ''): string =>
  `<path d="${d}" fill="none" stroke="currentColor" stroke-width="1.6" `
  + `stroke-linecap="round" stroke-linejoin="round"${extra} />`;

const RESIST_IDS = new Set(['mag_resist', 'ment_resist', 'arc_resist']);

/** The glyph and wording for one running effect, or null if it should not show. */
export function badgeFor(e: ActiveEffect): StatusBadge | null {
  const spell = SPELLS_BY_ID[e.spellId];
  const total = e.total && e.total > 0 ? e.total : Math.max(e.remaining, 0.001);
  const common = {
    fraction: Math.max(0, Math.min(1, e.remaining / total)),
    seconds: Math.max(0, e.remaining),
  };

  // Burning is not a spell anyone selects, so it has no catalogue entry.
  if (e.spellId === 'burning') {
    return {
      key: 'burning', label: 'Burning', tone: 'harm', element: 'fire',
      svg: stroke(GLYPH.flame), ...common,
    };
  }

  if (e.spellId === 'prayer') {
    return {
      key: 'prayer', label: 'Prayer', tone: 'guard',
      svg: stroke(GLYPH.halo) + stroke(GLYPH.rays), ...common,
    };
  }

  if (e.protection && spell) {
    // The element is the whole point of a protection, so it names it.
    const choices = spell.elementChoices ?? [];
    const index = e.element ? choices.indexOf(e.element) : 0;
    const layered = e.spellId === 'ment_armor';
    return {
      key: e.spellId + (e.element ?? '') + (e.sourceId ?? ''),
      label: displayName(spell, 1, index < 0 ? 0 : index),
      tone: 'guard',
      element: e.element,
      svg: stroke(GLYPH.shield) + (layered ? stroke(GLYPH.shieldInner) : ''),
      ...common,
    };
  }

  if (e.resist !== undefined && e.resist < 0) {
    return {
      key: e.spellId, label: spell?.name ?? 'Vulnerable', tone: 'harm',
      svg: stroke(GLYPH.crack), ...common,
    };
  }

  if (e.slow !== undefined) {
    const rooted = e.slow <= 0;
    return {
      key: e.spellId, label: spell?.name ?? (rooted ? 'Rooted' : 'Slowed'), tone: 'harm',
      svg: stroke(rooted ? GLYPH.shackle : GLYPH.chevrons), ...common,
    };
  }

  if (e.dps) {
    return {
      key: e.spellId, label: spell?.name ?? 'Bleeding', tone: 'harm',
      svg: stroke(GLYPH.drop), ...common,
    };
  }

  if (RESIST_IDS.has(e.spellId)) return null;
  return null;
}

/** Longest-running first, so a badge does not jump about as others expire. */
export function badgesFor(effects: readonly ActiveEffect[]): StatusBadge[] {
  return effects
    .map(badgeFor)
    .filter((b): b is StatusBadge => b !== null)
    .sort((a, b) => (a.tone === b.tone ? a.key.localeCompare(b.key) : a.tone === 'guard' ? -1 : 1));
}

export { ELEMENT_LABEL };
